import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Container, PageHeader, GenerateButton } from "@/components/shared";
import { toast } from "sonner";
import AdminKnowledge from "@/components/AdminKnowledge";
import AdminBooks from "@/components/AdminBooks";
import AdminPartners from "@/components/AdminPartners";
import AdminModeration from "@/components/AdminModeration";
import AdminQuotes from "@/components/AdminQuotes";
import AdminAfroMap from "@/components/AdminAfroMap";
import AdminKidsEvents from "@/components/AdminKidsEvents";
import AdminCommunity from "@/components/AdminCommunity";
import {
  Users, Crown, Rocket, Euro, Bug, UserCheck, Activity, Plus, Bell, Copy, Trash2, Send, Link2,
  Coins, TrendingUp, Lock, Globe, UserPlus, Power, ChevronDown, Star, Download, Check, Heart, Lightbulb,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [subs, setSubs] = useState([]);
  const [ai, setAi] = useState(null);
  const [settings, setSettings] = useState(null);
  const [note, setNote] = useState({ title: "", body: "", type: "info" });
  const [news, setNews] = useState([]);
  const [newsForm, setNewsForm] = useState({ title: "", category: "Discriminations", summary: "", body: "", source: "" });
  const [invite, setInvite] = useState({ kind: "public", label: "", name: "", plan: "premium", grant_role: "", max_uses: 50, expires_in_days: 30 });
  const [openSignups, setOpenSignups] = useState(null);
  const [fbModules, setFbModules] = useState([]);
  const [fbSummary, setFbSummary] = useState(null);
  const [fbFilter, setFbFilter] = useState({ module: "", rating: "", type: "", status: "" });
  const [stripeInfo, setStripeInfo] = useState(null);
  const [payFlags, setPayFlags] = useState([]);

  const loadFeedback = (filter = fbFilter) => {
    const p = new URLSearchParams();
    if (filter.module) p.set("module", filter.module);
    if (filter.rating) p.set("rating", filter.rating);
    if (filter.type) p.set("type", filter.type);
    if (filter.status) p.set("status", filter.status);
    api.get(`/admin/feedback?${p.toString()}`).then(({ data }) => {
      setFeedback(data.feedback || []);
      if (data.modules) setFbModules(data.modules);
    }).catch(() => {});
    api.get("/admin/feedback/summary").then(({ data }) => setFbSummary(data)).catch(() => {});
  };

  const markResolved = async (id, status) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
      setFeedback((prev) => prev.map((f) => (f.feedback_id === id ? { ...f, status } : f)));
      toast.success(status === "resolved" ? "Marqué comme traité." : "Ré-ouvert.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportFeedbackCsv = async () => {
    try {
      const res = await api.get("/admin/feedback/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "bizarre-retours.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiErr(e)));
    api.get("/admin/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
    api.get("/admin/invites").then(({ data }) => setInvites(data.invites || [])).catch(() => {});
    loadFeedback();
    api.get("/admin/subscriptions").then(({ data }) => setSubs(data.transactions || [])).catch(() => {});
    api.get("/admin/ai-costs").then(({ data }) => setAi(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/news").then(({ data }) => setNews(data.news || [])).catch(() => {});
    api.get("/admin/stripe-mode").then(({ data }) => setStripeInfo(data)).catch(() => {});
    api.get("/admin/payment-flags").then(({ data }) => setPayFlags(data.flags || [])).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const updateSettings = async (patch) => {
    try { const { data } = await api.post("/admin/settings", patch); setSettings(data); toast.success("Réglages mis à jour."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const setPlan = async (u, plan) => {
    try { await api.post(`/admin/users/${u.user_id}/plan`, { plan }); toast.success(`${u.email} → ${plan}`); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createInvite = async () => {
    if (invite.kind === "named" && !invite.name.trim()) return toast.error("Indiquez le nom de la personne.");
    const payload = invite.kind === "named"
      ? { kind: "named", name: invite.name.trim(), plan: invite.plan, grant_role: invite.grant_role || null, expires_in_days: invite.expires_in_days }
      : { kind: "public", label: invite.label.trim(), plan: invite.plan, grant_role: invite.grant_role || null, max_uses: invite.max_uses, expires_in_days: invite.expires_in_days };
    try {
      const { data } = await api.post("/admin/invites", payload);
      navigator.clipboard.writeText(`${window.location.origin}/login?invite=${data.code}`).catch(() => {});
      toast.success("Lien créé et copié dans le presse-papier.");
      setInvite({ ...invite, label: "", name: "" });
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const patchInvite = async (code, patch) => {
    try { await api.patch(`/admin/invites/${code}`, patch); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delInvite = async (code) => {
    if (!window.confirm("Supprimer définitivement ce lien ? Les inscrits existants ne sont pas supprimés.")) return;
    try { await api.delete(`/admin/invites/${code}`); loadAll(); } catch (e) { toast.error(apiErr(e)); }
  };
  const copyInvite = (code) => { navigator.clipboard.writeText(`${window.location.origin}/login?invite=${code}`); toast.success("Lien d'invitation copié !"); };
  const sendNote = async () => {
    if (!note.title || !note.body) return toast.error("Titre et message requis.");
    try { await api.post("/admin/notification", note); toast.success("Notification envoyée."); setNote({ title: "", body: "", type: "info" }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createNews = async () => {
    if (!newsForm.title || !newsForm.body) return toast.error("Titre et contenu requis.");
    try { await api.post("/admin/news", newsForm); toast.success("Actualité publiée."); setNewsForm({ title: "", category: "Discriminations", summary: "", body: "", source: "" }); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delNews = async (id) => { try { await api.delete(`/admin/news/${id}`); loadAll(); } catch (e) { toast.error(apiErr(e)); } };

  const STAT_CARDS = stats ? [
    { label: "Testeurs inscrits", value: stats.testers, icon: UserCheck },
    { label: "Utilisateurs", value: stats.users, icon: Users },
    { label: "Actifs (7j)", value: stats.active_7d, icon: Activity },
    { label: "Abonnements actifs", value: stats.active_subs, icon: Crown },
    { label: "Premium", value: stats.premium, icon: Crown },
    { label: "Pro", value: stats.pro, icon: Rocket },
    { label: "Revenus encaissés (€)", value: stats.revenue_real, icon: Euro },
    { label: "Revenus estimés (€)", value: stats.revenue, icon: Euro },
    { label: "Paiements", value: stats.paid_count, icon: Coins },
    { label: "Bugs ouverts", value: stats.open_bugs, icon: Bug },
  ] : [];

  return (
    <Container className="max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader eyebrow="Administration" title="Tableau de bord" />
        {stripeInfo && (() => {
          const live = stripeInfo.mode === "live" && stripeInfo.ready;
          const test = stripeInfo.mode === "test" && stripeInfo.ready;
          const label = live ? "Stripe LIVE" : test ? "Stripe TEST" : stripeInfo.mode === "placeholder" ? "Stripe NON CONFIGURÉ (clé de démo)" : "Stripe NON CONFIGURÉ";
          const cls = live
            ? "border-green-400/40 text-green-300 bg-green-500/10"
            : test
              ? "border-amber-400/40 text-amber-300 bg-amber-500/10"
              : "border-red-400/40 text-red-300 bg-red-500/10";
          return (
            <div>
              <div data-testid="admin-stripe-mode" title={`Webhook ${stripeInfo.webhook_configured ? "configuré" : "non configuré"}`}
                className={`mt-1 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${cls}`}>
                <span className={`w-2 h-2 rounded-full ${live ? "bg-green-400" : test ? "bg-amber-400" : "bg-red-400"}`} />
                {label}
                <span className="opacity-60">· webhook {stripeInfo.webhook_configured ? "✓" : "✗"}</span>
              </div>
              {stripeInfo.diagnostic && (
                <p data-testid="admin-stripe-diag" className="text-white/35 text-[10px] mt-1.5 font-mono">
                  clé: {stripeInfo.diagnostic.source_var} · préfixe {stripeInfo.diagnostic.key_prefix || "—"} · longueur {stripeInfo.diagnostic.key_length} · webhook {stripeInfo.diagnostic.webhook_present ? "présent" : "absent"}{stripeInfo.diagnostic.had_whitespace ? " · ⚠️ espaces parasites" : ""}
                </p>
              )}
            </div>
          );
        })()}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {STAT_CARDS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} data-testid={`admin-stat-${s.label}`} className="card-surface p-5">
              <Icon size={18} className="text-gold mb-2" strokeWidth={1.5} />
              <div className="font-display text-2xl">{s.value}</div>
              <div className="text-white/40 text-xs mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {stats?.top_features && (
        <div className="card-surface p-5 mb-8">
          <p className="text-gold text-xs uppercase tracking-[0.15em] mb-3">Fonctionnalités les plus utilisées</p>
          <div className="space-y-2">
            {stats.top_features.map((f) => {
              const max = Math.max(1, ...stats.top_features.map((x) => x.count));
              return (
                <div key={f.name} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-white/60 shrink-0">{f.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full gold-gradient" style={{ width: `${(f.count / max) * 100}%` }} />
                  </div>
                  <span className="text-sm text-white/50 w-8 text-right">{f.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Tabs defaultValue="users">
        <TabsList className="bg-white/[0.04] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">Testeurs & plans</TabsTrigger>
          <TabsTrigger value="invites" data-testid="admin-tab-invites">Invitations</TabsTrigger>
          <TabsTrigger value="access" data-testid="admin-tab-access">Accès & inscriptions</TabsTrigger>
          <TabsTrigger value="news" data-testid="admin-tab-news">Actualités</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="admin-tab-knowledge">Base de connaissances</TabsTrigger>
          <TabsTrigger value="books" data-testid="admin-tab-books">Bibliothèque (Livres)</TabsTrigger>
          <TabsTrigger value="partners" data-testid="admin-tab-partners">Partenaires</TabsTrigger>
          <TabsTrigger value="aicosts" data-testid="admin-tab-aicosts">Coûts IA</TabsTrigger>
          <TabsTrigger value="bugs" data-testid="admin-tab-bugs">Retours utilisateurs</TabsTrigger>
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation">Modération</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Citation du jour</TabsTrigger>
          <TabsTrigger value="afromap" data-testid="admin-tab-afromap">Carte Afro</TabsTrigger>
          <TabsTrigger value="kids-events" data-testid="admin-tab-kids-events">Événements Kids</TabsTrigger>
          <TabsTrigger value="community-space" data-testid="admin-tab-community-space">Espace Communauté</TabsTrigger>
          <TabsTrigger value="notif" data-testid="admin-tab-notif">Notifications</TabsTrigger>
          <TabsTrigger value="subs" data-testid="admin-tab-subs">Abonnements</TabsTrigger>
        </TabsList>

        {/* MODÉRATION */}
        <TabsContent value="moderation" className="mt-5">
          <AdminModeration />
        </TabsContent>

        <TabsContent value="quotes" className="mt-5">
          <AdminQuotes />
        </TabsContent>

        <TabsContent value="afromap" className="mt-5">
          <AdminAfroMap />
        </TabsContent>
        <TabsContent value="kids-events" className="mt-5">
          <AdminKidsEvents />
        </TabsContent>

        <TabsContent value="community-space" className="mt-5">
          <AdminCommunity />
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-5">
          <div className="card-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-white/40 border-b border-white/10">
                <tr><th className="text-left p-3">Utilisateur</th><th className="text-left p-3">Bêta</th><th className="text-left p-3">Plan</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`admin-user-${u.email}`} className="border-b border-white/5">
                    <td className="p-3"><div className="text-white/80">{u.name}</div><div className="text-white/40 text-xs">{u.email}</div></td>
                    <td className="p-3">{u.is_beta ? <span className="text-gold text-xs">Bêta</span> : <span className="text-white/30 text-xs">—</span>}</td>
                    <td className="p-3">
                      <select data-testid={`plan-select-${u.email}`} value={u.plan || "free"} onChange={(e) => setPlan(u, e.target.value)}
                        className="bg-white/[0.06] border border-white/15 rounded-lg px-2 py-1 text-sm outline-none">
                        <option value="free">Gratuit</option>
                        <option value="premium">Premium</option>
                        <option value="pro">Pro</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* INVITES */}
        <TabsContent value="invites" className="mt-5 space-y-4">
          <div className="card-surface p-5 max-w-3xl">
            <div className="flex gap-2 mb-4">
              <button data-testid="invite-kind-public" onClick={() => setInvite({ ...invite, kind: "public" })}
                className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm border transition-colors ${invite.kind === "public" ? "border-gold/50 bg-gold/10 text-gold" : "border-white/10 bg-white/[0.03] text-white/60 hover:text-white"}`}>
                <Globe size={16} /> Lien public
              </button>
              <button data-testid="invite-kind-named" onClick={() => setInvite({ ...invite, kind: "named" })}
                className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm border transition-colors ${invite.kind === "named" ? "border-gold/50 bg-gold/10 text-gold" : "border-white/10 bg-white/[0.03] text-white/60 hover:text-white"}`}>
                <UserPlus size={16} /> Lien nominatif
              </button>
            </div>
            <p className="text-white/60 text-sm mb-3">
              {invite.kind === "public"
                ? "Lien à partager en story, réseaux sociaux ou e-mail. Choisissez le nombre d'inscriptions et l'expiration."
                : "Lien personnel pour une personne précise. Utilisable une seule fois. Vous pourrez le désactiver à tout moment."}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
              {invite.kind === "public" ? (
                <input data-testid="invite-label" placeholder="Libellé (ex. Story Instagram)" value={invite.label} onChange={(e) => setInvite({ ...invite, label: e.target.value })}
                  className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm" />
              ) : (
                <input data-testid="invite-name" placeholder="Nom de la personne (ex. Kalash)" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                  className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm" />
              )}
              <select data-testid="invite-plan" value={invite.plan} onChange={(e) => setInvite({ ...invite, plan: e.target.value })}
                className="bg-white/[0.06] border border-white/15 rounded-xl px-3 py-2 text-sm outline-none">
                <option value="free">Gratuit</option>
                <option value="premium">Premium</option>
                <option value="pro">Pro</option>
              </select>
              <select data-testid="invite-grant-role" value={invite.grant_role} onChange={(e) => setInvite({ ...invite, grant_role: e.target.value })}
                className="bg-white/[0.06] border border-white/15 rounded-xl px-3 py-2 text-sm outline-none">
                <option value="">Rôle : Membre (normal)</option>
                <option value="partner">Rôle : Partenaire</option>
                <option value="ambassador">Rôle : Ambassadeur</option>
              </select>
              {invite.kind === "public" && (
                <select data-testid="invite-max-uses" value={invite.max_uses ?? ""} onChange={(e) => setInvite({ ...invite, max_uses: e.target.value === "" ? null : parseInt(e.target.value) })}
                  className="bg-white/[0.06] border border-white/15 rounded-xl px-3 py-2 text-sm outline-none">
                  <option value="10">10 inscriptions</option>
                  <option value="50">50 inscriptions</option>
                  <option value="100">100 inscriptions</option>
                  <option value="500">500 inscriptions</option>
                  <option value="">Inscriptions illimitées</option>
                </select>
              )}
              <select data-testid="invite-expiry" value={invite.expires_in_days ?? ""} onChange={(e) => setInvite({ ...invite, expires_in_days: e.target.value === "" ? null : parseInt(e.target.value) })}
                className="bg-white/[0.06] border border-white/15 rounded-xl px-3 py-2 text-sm outline-none">
                <option value="">N'expire jamais</option>
                <option value="1">Expire dans 1 jour</option>
                <option value="7">Expire dans 7 jours</option>
                <option value="30">Expire dans 30 jours</option>
                <option value="90">Expire dans 90 jours</option>
              </select>
            </div>
            <GenerateButton data-testid="create-invite-button" onClick={createInvite}><Plus size={16} /> Générer le lien</GenerateButton>
          </div>
          <div className="space-y-2">
            {invites.map((iv) => {
              const badge = iv.status === "valid" ? { t: "Actif", c: "bg-gold/15 text-gold" }
                : iv.status === "disabled" ? { t: "Désactivé", c: "bg-white/10 text-white/50" }
                : iv.status === "expired" ? { t: "Expiré", c: "bg-red-500/15 text-red-300" }
                : { t: "Épuisé", c: "bg-orange-500/15 text-orange-300" };
              const uses = iv.max_uses ? `${iv.used_count}/${iv.max_uses}` : `${iv.used_count}/∞`;
              const exp = iv.expires_at ? new Date(iv.expires_at).toLocaleDateString("fr-FR") : "jamais";
              const isNamed = iv.kind === "named";
              const title = isNamed ? (iv.name || "Sans nom") : (iv.label || "Lien public");
              const active = iv.active !== false;
              return (
                <div key={iv.code} data-testid={`invite-${iv.code}`} className="card-surface p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm truncate flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isNamed ? "bg-white/10 text-white/70" : "bg-gold/10 text-gold"}`}>
                          {isNamed ? <UserPlus size={10} /> : <Globe size={10} />}{isNamed ? "Nominatif" : "Public"}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge.c}`}>{badge.t}</span>
                        {iv.grant_role && (
                          <span data-testid={`invite-role-${iv.code}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">
                            {iv.grant_role === "partner" ? "Partenaire" : iv.grant_role === "ambassador" ? "Ambassadeur" : iv.grant_role}
                          </span>
                        )}
                        <span className="text-white font-medium truncate">{title}</span>
                      </p>
                      <p className="text-white/40 text-xs mt-1">
                        {iv.used_count || 0} inscrit{(iv.used_count || 0) > 1 ? "s" : ""} · Utilisations {uses} · Expire : {exp}
                      </p>
                      <p className="text-white/30 text-xs truncate">{window.location.origin}/login?invite={iv.code}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select data-testid={`invite-plan-${iv.code}`} value={iv.plan} onChange={(e) => patchInvite(iv.code, { plan: e.target.value })}
                        className="bg-white/[0.06] border border-white/15 rounded-lg px-2 py-1 text-xs outline-none">
                        <option value="free">Gratuit</option>
                        <option value="premium">Premium</option>
                        <option value="pro">Pro</option>
                      </select>
                      <button title="Copier le lien" data-testid={`copy-invite-${iv.code}`} onClick={() => copyInvite(iv.code)} className="text-white/50 hover:text-gold"><Link2 size={16} /></button>
                      <button title={active ? "Désactiver" : "Réactiver"} data-testid={`toggle-invite-${iv.code}`} onClick={() => patchInvite(iv.code, { active: !active })}
                        className={active ? "text-gold hover:text-gold/70" : "text-white/40 hover:text-white"}><Power size={16} /></button>
                      <button title="Supprimer" data-testid={`del-invite-${iv.code}`} onClick={() => delInvite(iv.code)} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  {(iv.signups || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <button data-testid={`signups-toggle-${iv.code}`} onClick={() => setOpenSignups(openSignups === iv.code ? null : iv.code)}
                        className="flex items-center gap-1.5 text-xs text-white/60 hover:text-gold">
                        <ChevronDown size={14} className={`transition-transform ${openSignups === iv.code ? "rotate-180" : ""}`} />
                        Voir les {iv.signups.length} inscrit{iv.signups.length > 1 ? "s" : ""}
                      </button>
                      {openSignups === iv.code && (
                        <div className="mt-2 space-y-1" data-testid={`signups-list-${iv.code}`}>
                          {iv.signups.map((s, i) => (
                            <div key={i} className="flex items-center justify-between text-xs bg-white/[0.03] rounded-lg px-3 py-1.5">
                              <span className="text-white/80 truncate">{s.name || "—"} · <span className="text-white/40">{s.email}</span></span>
                              <span className="text-gold shrink-0">{s.plan || iv.plan}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {invites.length === 0 && <p className="text-white/30 text-sm">Aucune invitation.</p>}
          </div>
        </TabsContent>

        {/* AI COSTS + BETA SETTINGS */}
        {/* ACTUALITÉS */}
        <TabsContent value="news" className="mt-5 space-y-4">
          <div className="card-surface p-5 max-w-3xl">
            <p className="text-white/60 text-sm mb-3">Publiez une évolution du droit. Elle apparaît immédiatement dans « Actualités » pour les utilisateurs.</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <input data-testid="news-title" placeholder="Titre" value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm" />
              <select data-testid="news-category" value={newsForm.category} onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                className="bg-white/[0.06] border border-white/15 rounded-xl px-3 py-2 text-sm outline-none">
                <option>Racisme</option>
                <option>Discriminations</option>
                <option>Libertés publiques</option>
                <option>Droits des citoyens</option>
              </select>
            </div>
            <input data-testid="news-summary" placeholder="Résumé court (optionnel)" value={newsForm.summary} onChange={(e) => setNewsForm({ ...newsForm, summary: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm mb-3" />
            <textarea data-testid="news-body" rows={4} placeholder="Contenu de l'actualité" value={newsForm.body} onChange={(e) => setNewsForm({ ...newsForm, body: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm resize-none mb-3" />
            <input data-testid="news-source" placeholder="Source (optionnel)" value={newsForm.source} onChange={(e) => setNewsForm({ ...newsForm, source: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm mb-3" />
            <GenerateButton data-testid="create-news-button" onClick={createNews}><Plus size={16} /> Publier</GenerateButton>
          </div>
          <div className="space-y-2">
            {news.map((n) => (
              <div key={n.news_id} data-testid={`admin-news-${n.news_id}`} className="card-surface p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm truncate"><span className="text-gold text-xs mr-2">{n.category}</span>{n.title}</p>
                  <p className="text-white/35 text-xs mt-0.5">{new Date(n.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <button data-testid={`del-news-${n.news_id}`} onClick={() => delNews(n.news_id)} className="text-white/50 hover:text-red-400 shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
            {news.length === 0 && <p className="text-white/30 text-sm">Aucune actualité publiée.</p>}
          </div>
        </TabsContent>

        {/* KNOWLEDGE / BIBLIOTHÈQUE */}
        <TabsContent value="knowledge" className="mt-5">
          <AdminKnowledge />
        </TabsContent>

        <TabsContent value="books" className="mt-5">
          <AdminBooks />
        </TabsContent>

        <TabsContent value="partners" className="mt-5">
          <AdminPartners />
        </TabsContent>

        {/* ACCÈS & INSCRIPTIONS */}
        <TabsContent value="access" className="mt-5 space-y-6">
          {settings && (
            <div className="card-surface p-5 space-y-5" data-testid="access-settings">
              <p className="text-gold text-xs uppercase tracking-[0.15em] flex items-center gap-2"><Lock size={14} /> Accès & inscriptions</p>

              <div className="flex items-start gap-3">
                <button data-testid="toggle-invite-only" onClick={() => updateSettings({ invite_only: !settings.invite_only })}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 mt-0.5 ${settings.invite_only ? "bg-gold" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-transform ${settings.invite_only ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <div>
                  <p className="text-sm font-medium">Bêta privée (sur invitation uniquement)</p>
                  <p className="text-white/45 text-xs mt-1">
                    {settings.invite_only
                      ? "Activée : seules les personnes disposant d'un lien d'invitation peuvent créer un compte."
                      : "Désactivée : toute personne peut créer un compte sur bizarreai.fr (Google ou e-mail + mot de passe), sans invitation."}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <button data-testid="toggle-confidential" onClick={() => updateSettings({ confidential_notice: !settings.confidential_notice })}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 mt-0.5 ${settings.confidential_notice ? "bg-gold" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-transform ${settings.confidential_notice ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <div>
                  <p className="text-sm font-medium">Message de confidentialité</p>
                  <p className="text-white/45 text-xs mt-1">
                    {settings.confidential_notice
                      ? "Activé : l'écran « bêta privée / accès réservé » s'affiche pour les visiteurs."
                      : "Désactivé : aucun écran ni message « bêta privée », « bêta-testeur », « confidentiel » ou « accès réservé » n'apparaît."}
                  </p>
                </div>
              </div>

              <p className="text-white/40 text-xs border-t border-white/10 pt-4">
                💡 Le système de liens d'invitation reste entièrement disponible dans l'onglet « Invitations » (génération, quotas, expiration, plan associé). Les invitations servent à vos accès spéciaux ; elles ne sont plus obligatoires lorsque la bêta privée est désactivée.
              </p>
            </div>
          )}
        </TabsContent>

        {/* AI COSTS */}
        <TabsContent value="aicosts" className="mt-5 space-y-6">
          {settings && (
            <div className="card-surface p-5">
              <p className="text-gold text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2"><Lock size={14} /> Budget IA</p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-white/50">Budget IA / mois ($)</span>
                <input data-testid="ai-budget-input" type="number" defaultValue={settings.ai_budget_usd}
                  onBlur={(e) => updateSettings({ ai_budget_usd: parseFloat(e.target.value) })}
                  className="w-24 bg-white/[0.06] border border-white/15 rounded-lg px-2 py-1 outline-none" />
              </div>
            </div>
          )}

          {ai && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-2xl p-5 border border-gold/40 bg-gradient-to-b from-[#161206] to-[#050505]" data-testid="ai-credits-remaining">
                  <Coins size={18} className="text-gold mb-2" />
                  <div className="font-display text-2xl gold-text">${ai.remaining_usd}</div>
                  <div className="text-white/40 text-xs mt-1">Crédits restants (budget ${ai.budget_usd})</div>
                </div>
                <div className="card-surface p-5">
                  <Euro size={18} className="text-gold mb-2" />
                  <div className="font-display text-2xl">${ai.spend_today}</div>
                  <div className="text-white/40 text-xs mt-1">Dépense aujourd'hui</div>
                </div>
                <div className="card-surface p-5">
                  <Euro size={18} className="text-gold mb-2" />
                  <div className="font-display text-2xl">${ai.spend_month}</div>
                  <div className="text-white/40 text-xs mt-1">Dépense ce mois</div>
                </div>
                <div className="card-surface p-5">
                  <TrendingUp size={18} className="text-gold mb-2" />
                  <div className="font-display text-2xl">${ai.projected_month}</div>
                  <div className="text-white/40 text-xs mt-1">Projection fin de mois</div>
                </div>
              </div>
              <p className="text-white/30 text-xs">Estimation basée sur ~{ai.cost_per_1k_usd}$/1k tokens · {ai.tokens_month} tokens ce mois · {ai.calls_month} appels. La consommation réelle de la clé Universelle se consulte dans Profil → Universal Key.</p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="card-surface p-5">
                  <p className="text-gold text-xs uppercase tracking-[0.15em] mb-3">Consommation / jour (14j)</p>
                  <div className="flex items-end gap-1 h-32">
                    {ai.by_day.map((d) => {
                      const max = Math.max(0.0001, ...ai.by_day.map((x) => x.cost));
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center justify-end group" title={`${d.date}: $${d.cost} (${d.calls})`}>
                          <div className="w-full rounded-t gold-gradient" style={{ height: `${(d.cost / max) * 100}%`, minHeight: d.cost > 0 ? 3 : 0 }} />
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-white/30 text-xs mt-2">{ai.by_day[0]?.date} → {ai.by_day[ai.by_day.length - 1]?.date}</p>
                </div>

                <div className="card-surface p-5">
                  <p className="text-gold text-xs uppercase tracking-[0.15em] mb-3">Par fonctionnalité</p>
                  <div className="space-y-2">
                    {ai.by_feature.length === 0 && <p className="text-white/30 text-sm">Aucune donnée.</p>}
                    {ai.by_feature.map((f) => {
                      const max = Math.max(0.0001, ...ai.by_feature.map((x) => x.cost));
                      return (
                        <div key={f.feature} className="flex items-center gap-3">
                          <span className="w-24 text-sm text-white/60 shrink-0 capitalize">{f.feature}</span>
                          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full gold-gradient" style={{ width: `${(f.cost / max) * 100}%` }} />
                          </div>
                          <span className="text-xs text-white/50 w-20 text-right">${f.cost} · {f.calls}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="card-surface overflow-hidden">
                <p className="text-gold text-xs uppercase tracking-[0.15em] p-4 pb-2">Consommation par utilisateur (top 15)</p>
                <table className="w-full text-sm">
                  <thead className="text-white/40 border-b border-white/10">
                    <tr><th className="text-left p-3">Utilisateur</th><th className="text-left p-3">Appels</th><th className="text-left p-3">Coût estimé</th></tr>
                  </thead>
                  <tbody>
                    {ai.by_user.map((u) => (
                      <tr key={u.user_id} className="border-b border-white/5">
                        <td className="p-3 text-white/70">{u.email}</td>
                        <td className="p-3">{u.calls}</td>
                        <td className="p-3 text-gold">${u.cost}</td>
                      </tr>
                    ))}
                    {ai.by_user.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-white/30">Aucune consommation.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        {/* RETOURS UTILISATEURS */}
        <TabsContent value="bugs" className="mt-5 space-y-4">
          {fbSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="feedback-summary">
              <div className="card-surface p-4 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Star size={18} className="fill-gold text-gold" />
                  <span className="font-display text-2xl gold-text">{fbSummary.avg_rating || "—"}</span>
                </div>
                <p className="text-white/45 text-xs">Note moyenne ({fbSummary.rating_count})</p>
              </div>
              <div className="card-surface p-4 text-center">
                <p className="font-display text-2xl">{fbSummary.total}</p>
                <p className="text-white/45 text-xs">Retours reçus</p>
              </div>
              <div className="card-surface p-4 text-center">
                <p className="font-display text-2xl text-emerald-300">{fbSummary.open}</p>
                <p className="text-white/45 text-xs">À traiter</p>
              </div>
              <div className="card-surface p-4 text-center">
                <p className="font-display text-2xl text-gold">{(fbSummary.by_type?.suggestion || 0) + (fbSummary.by_type?.feature || 0)}</p>
                <p className="text-white/45 text-xs">Idées & fonctionnalités</p>
              </div>
              {fbSummary.top_modules?.length > 0 && (
                <div className="col-span-2 md:col-span-4 card-surface p-4">
                  <p className="text-white/50 text-xs mb-2">Modules les plus commentés</p>
                  <div className="flex flex-wrap gap-2">
                    {fbSummary.top_modules.map((m) => (
                      <span key={m.module} className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/10 px-3 py-1 text-xs text-white/70">
                        {m.module} <span className="text-gold">{m.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="card-surface p-4 flex flex-wrap items-end gap-3" data-testid="feedback-filters">
            <div>
              <label className="block text-xs text-white/40 mb-1">Type</label>
              <select data-testid="fb-filter-type" value={fbFilter.type}
                onChange={(e) => { const f = { ...fbFilter, type: e.target.value }; setFbFilter(f); loadFeedback(f); }}
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="" className="bg-[#0a0a0a]">Tous</option>
                <option value="compliment" className="bg-[#0a0a0a]">J'aime</option>
                <option value="suggestion" className="bg-[#0a0a0a]">Idée</option>
                <option value="feature" className="bg-[#0a0a0a]">Fonctionnalité</option>
                <option value="bug" className="bg-[#0a0a0a]">Bug</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Note</label>
              <select data-testid="fb-filter-rating" value={fbFilter.rating}
                onChange={(e) => { const f = { ...fbFilter, rating: e.target.value }; setFbFilter(f); loadFeedback(f); }}
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="" className="bg-[#0a0a0a]">Toutes</option>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n} className="bg-[#0a0a0a]">{n} ★</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Module</label>
              <select data-testid="fb-filter-module" value={fbFilter.module}
                onChange={(e) => { const f = { ...fbFilter, module: e.target.value }; setFbFilter(f); loadFeedback(f); }}
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="" className="bg-[#0a0a0a]">Tous</option>
                {fbModules.map((m) => <option key={m} value={m} className="bg-[#0a0a0a]">{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Statut</label>
              <select data-testid="fb-filter-status" value={fbFilter.status}
                onChange={(e) => { const f = { ...fbFilter, status: e.target.value }; setFbFilter(f); loadFeedback(f); }}
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="" className="bg-[#0a0a0a]">Tous</option>
                <option value="open" className="bg-[#0a0a0a]">À traiter</option>
                <option value="resolved" className="bg-[#0a0a0a]">Traité</option>
              </select>
            </div>
            <button data-testid="fb-export-csv" onClick={exportFeedbackCsv}
              className="ml-auto inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 text-sm hover:border-gold/40 hover:text-gold transition-colors">
              <Download size={15} /> Exporter CSV
            </button>
          </div>

          {feedback.map((f) => {
            const TYPE = { bug: { c: "bg-red-500/15 text-red-300", l: "🐞 Bug" },
                           suggestion: { c: "bg-gold/15 text-gold", l: "💡 Idée" },
                           feature: { c: "bg-blue-500/15 text-blue-300", l: "🚀 Fonctionnalité" },
                           compliment: { c: "bg-emerald-500/15 text-emerald-300", l: "💛 J'aime" } }[f.type]
                           || { c: "bg-white/10 text-white/60", l: f.type };
            return (
            <div key={f.feedback_id} data-testid={`feedback-item-${f.feedback_id}`} className="card-surface p-5">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE.c}`}>{TYPE.l}</span>
                {f.rating ? (
                  <span className="inline-flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= f.rating ? "fill-gold text-gold" : "text-white/20"} />)}
                  </span>
                ) : null}
                {f.module && <span className="text-xs rounded-full bg-white/[0.06] border border-white/10 px-2 py-0.5 text-white/60">{f.module}</span>}
                {f.status === "resolved" && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">✓ Traité</span>}
                <span className="text-white/30 text-xs ml-auto">{f.email} · v{f.app_version || "?"} · {new Date(f.created_at).toLocaleString("fr-FR")}</span>
              </div>
              {f.liked && <p className="text-white/80 text-sm">👍 {f.liked}</p>}
              {f.disliked && <p className="text-white/80 text-sm mt-1">👎 {f.disliked}</p>}
              {f.improvement && <p className="text-gold/90 text-sm mt-1">💡 {f.improvement}</p>}
              {f.description && <p className="text-red-200/90 text-sm mt-1">🐞 {f.description}</p>}
              {(f.device || f.page) && <p className="text-white/30 text-xs mt-2">📱 {f.device} · {f.page}</p>}
              {f.screenshot_base64 && <img src={f.screenshot_base64} alt="capture" className="mt-3 max-h-48 rounded-lg border border-white/10" />}
              <div className="mt-3">
                {f.status === "resolved" ? (
                  <button data-testid={`fb-reopen-${f.feedback_id}`} onClick={() => markResolved(f.feedback_id, "open")}
                    className="text-xs text-white/50 hover:text-white border border-white/15 rounded-full px-3 py-1.5">Ré-ouvrir</button>
                ) : (
                  <button data-testid={`fb-resolve-${f.feedback_id}`} onClick={() => markResolved(f.feedback_id, "resolved")}
                    className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-full px-3 py-1.5">
                    <Check size={13} /> Marquer comme traité
                  </button>
                )}
              </div>
            </div>
            );
          })}
          {feedback.length === 0 && <p className="text-white/30 text-sm">Aucun retour pour l'instant.</p>}
        </TabsContent>

        {/* NOTIF */}
        <TabsContent value="notif" className="mt-5">
          <div className="card-surface p-5 space-y-3 max-w-2xl">
            <input data-testid="note-title" placeholder="Titre (ex : Nouvelle fonctionnalité)" value={note.title} onChange={(e) => setNote({ ...note, title: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm" />
            <textarea data-testid="note-body" rows={2} placeholder="Message" value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm resize-none" />
            <GenerateButton data-testid="note-send-button" onClick={sendNote}><Bell size={16} /> Envoyer</GenerateButton>
          </div>
        </TabsContent>

        {/* SUBS */}
        <TabsContent value="subs" className="mt-5">
          {payFlags.length > 0 && (
            <div className="card-surface p-4 mb-4" data-testid="admin-payment-flags">
              <p className="text-gold text-xs uppercase tracking-[0.15em] mb-3">Remboursements & anomalies à traiter</p>
              <div className="space-y-2">
                {payFlags.map((f) => (
                  <div key={f.flag_id} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-2">
                    <div>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${f.type === "refund_full" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
                        {f.type === "refund_full" ? "Remboursement total → Gratuit" : "Remboursement partiel (action manuelle)"}
                      </span>
                      <span className="text-white/50 ml-2">{f.amount_refunded} / {f.amount} {f.currency?.toUpperCase()} · {f.user_id || "?"}</span>
                    </div>
                    {!f.resolved ? (
                      <button data-testid={`resolve-flag-${f.flag_id}`} onClick={async () => { try { await api.post(`/admin/payment-flags/${f.flag_id}/resolve`); setPayFlags((p) => p.map((x) => x.flag_id === f.flag_id ? { ...x, resolved: true } : x)); } catch (e) { toast.error(apiErr(e)); } }}
                        className="shrink-0 text-xs border border-white/15 rounded-full px-3 py-1 hover:border-gold/40 hover:text-gold">Marquer traité</button>
                    ) : <span className="text-white/30 text-xs shrink-0">✓ Traité</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="card-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-white/40 border-b border-white/10">
                <tr><th className="text-left p-3">Utilisateur</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Montant</th><th className="text-left p-3">Statut</th><th className="text-left p-3">Date</th></tr>
              </thead>
              <tbody>
                {subs.map((t) => (
                  <tr key={t.session_id} className="border-b border-white/5">
                    <td className="p-3 text-white/70">{t.user_id}</td>
                    <td className="p-3">{t.plan || "-"}</td>
                    <td className="p-3">{t.amount} {t.currency?.toUpperCase()}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${t.payment_status === "paid" ? "bg-gold/15 text-gold" : "bg-white/5 text-white/50"}`}>{t.payment_status}</span></td>
                    <td className="p-3 text-white/40">{new Date(t.created_at).toLocaleDateString("fr-FR")}</td>
                  </tr>
                ))}
                {subs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/30">Aucun abonnement.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </Container>
  );
}
